import { MediaType, requestDownload } from '@qse/api'
import { ModelControlEnum } from '@qse/constants'
import { useActiveModelStore } from '@qse/stores'

export type PreviewStatus = 'PROCESSING' | 'DECRYPTING' | 'AVAILABLE' | 'FAILED' | 'NO_PREVIEW'

// Helper function to check if file type is supported for preview
export const isFilePreviewable = (mediaType: MediaType): boolean => {
  switch (mediaType) {
    case MediaType.IMAGE:
    case MediaType.VIDEO:
    case MediaType.PDF:
      return true
    case MediaType.DOCUMENT:
    case MediaType.SPREADSHEET:
    case MediaType.PRESENTATION:
    case MediaType.FOLDER:
    case MediaType.AUDIO:
    case MediaType.ARCHIVE:
    case MediaType.OTHER:
      return false
    default:
      return false
  }
}

// Helper function to check if file is an image
export const isImageFile = (mediaType: MediaType): boolean => {
  return mediaType === MediaType.IMAGE
}

// Helper function to check if file is a video
export const isVideoFile = (mediaType: MediaType): boolean => {
  return mediaType === MediaType.VIDEO
}

export const isSvgFile = (fileName: string): boolean => {
  return fileName.toLowerCase().endsWith('.svg')
}

// Helper function to check if file is a PDF
export const isPdfFile = (mediaType: MediaType): boolean => {
  return mediaType === MediaType.PDF
}

// Helper function to check if file is a text file
export const isTextFile = (mediaType: MediaType): boolean => {
  return mediaType === MediaType.DOCUMENT
}

// Helper function to check if file is a Word document
export const isWordFile = (mediaType: MediaType): boolean => {
  return mediaType === MediaType.DOCUMENT
}

// Helper function to check if file is a CSV file
export const isCSVFile = (mediaType: MediaType): boolean => {
  return mediaType === MediaType.SPREADSHEET
}

// Helper function to get file extension
export const getFilePreviewExtension = (fileName: string): string => {
  return fileName.split('.').pop()?.toLowerCase() || ''
}

// Reusable function to handle file preview
export const handleFilePreview = async (
  fileId: string | number,
  fileName: string,
  mediaType: MediaType,
  isDir: boolean,
  isFavorite: boolean,
  mimeType: string,
) => {
  const store = useActiveModelStore.getState()

  // Check if file is previewable
  const isPreviewable = isFilePreviewable(mediaType)

  store.setData({
    id: `preview-${fileId}`,
    fileId: String(fileId),
    name: fileName,
    mediaType: mediaType,
    status: isPreviewable ? 'PROCESSING' : 'NO_PREVIEW',
    isFolder: isDir,
    isFavorite: isFavorite,
    mimeType: mimeType,
  })

  // Hide global modal header so we don't double-render headers (our component draws its own)
  store.setModalHeader?.({
    title: '',
    subtitle: '',
    showCloseButton: false,
  })

  // Width is irrelevant because the component renders full-screen overlay.
  // Keep it if your modal system requires a size; otherwise you can remove this line.
  store.setWidth?.('max-w-full')

  store.setModelActive(ModelControlEnum.FILE_PREVIEW)
  store.toggle()

  // Skip download request for unsupported file types
  if (!isPreviewable) {
    return
  }

  try {
    // Request file access (same as download but we'll handle differently)
    const downloadResponse = await requestDownload({ id: String(fileId) })

    if (downloadResponse.status === 'AVAILABLE' && downloadResponse.url) {
      useActiveModelStore.setState({
        data: {
          ...useActiveModelStore.getState().data,
          url: downloadResponse.url,
          status: 'AVAILABLE',
        },
      })
    } else {
      useActiveModelStore.setState({
        data: {
          ...useActiveModelStore.getState().data,
          status: downloadResponse.status,
        },
      })
    }
  } catch (error) {
    console.error('Preview request failed:', error)
    useActiveModelStore.setState({
      data: {
        ...useActiveModelStore.getState().data,
        status: 'FAILED',
        errorMessage: error instanceof Error ? error.message : 'Preview failed',
      },
    })
  }
}
